import { request } from '@/api/apiUtils';
import { mergeCharacters } from '@/api/mutations/mergeCharacters';
import {
    absorbExtras,
    type CharacterExtras,
    DEFAULT_CREATE_CATEGORIES,
    removeExtras,
    resolveServerCategories,
    writeExtras,
} from '@/data/characterExtras';
import { CHARACTERS_ROOT } from '@/data/entities/characters';
import { getPrefixes, useCustomMutation, useDelete, useOptimisticPrefixPatch, useUpdate } from '@/data/mutations';
import { SignCharacter } from '@/enums';
import type { CreateCharacter, ICharacter, IRelationsCreate, ResponseType, UpdateCharacterType } from '@/types';

// Every cached characters list, regardless of the filter-key shape the consumer
// used (the editor cue ComboBox and the entity system key their params
// differently) — a prefix patch covers them all so optimistic inserts/removals
// are visible everywhere at once.
const CHARACTERS_LIST_PREFIX = [...CHARACTERS_ROOT, 'list'] as const;

// Session-monotonic negative ids mark optimistic entries; the real entity is
// swapped in by exact id on success (no wall-clock, no magnitude heuristics).
let optimisticCharacterSeq = 0;

interface CreateCharacterContext {
    previousLists: Array<[readonly unknown[], ICharacter[] | undefined]>;
    optimisticId: number;
}

export function useCreateCharacter() {
    const lists = useOptimisticPrefixPatch<ICharacter[]>(CHARACTERS_LIST_PREFIX);

    return useCustomMutation<{ project_id: number; data: CreateCharacter }, ResponseType<ICharacter>, CreateCharacterContext>({
        mutationFn: ({ project_id, data }) =>
            request<ResponseType<ICharacter>>(`projects/${project_id}/characters`, {
                method: 'POST',
                // The backend now REQUIRES a non-empty `categories` on create
                // (server label values); default it here so every creation
                // path — list button, editor cues, create-and-attach — stays valid.
                body: JSON.stringify({ categories: [...DEFAULT_CREATE_CATEGORIES], ...data }),
            }),
        onMutate: async ({ data }) => {
            const optimisticId = -(optimisticCharacterSeq += 2);
            const optimisticCharacter: ICharacter = {
                id: optimisticId,
                name: data.name,
                gender: data.gender || 'other',
                age_category: data.age_category || '',
                category: SignCharacter.secondary,
                status: 'active',
                entity_tag: {
                    id: optimisticId - 1,
                    name: data.name,
                    tag_category_id: 6, // characterTag category
                    color: '#000000',
                },
                assets: [],
                text_atoms: [],
                tags: [],
                script_name: null,
                full_name: null,
                alias: null,
                initial_goal: null,
                final_goal: null,
                description: null,
                role: null,
                line_relations: [],
                characteristics: [],
                importance: null,
                categories: [SignCharacter.secondary],
                characteristic: null,
                social_profession: null,
                social_income: null,
                social_education: null,
                social_family: null,
                personality: null,
                problems: null,
                motivation: null,
            };

            await lists.cancelInFlight();
            const previousLists = lists.snapshot();
            lists.patch(old => (old ? [...old, optimisticCharacter] : old));
            return { previousLists, optimisticId };
        },
        onError: (_err, _params, context) => {
            if (context) lists.rollback(context.previousLists);
        },
        onSuccess: (result, _params, context) => {
            if (result.success && result.data && context) {
                // Swap the optimistic entry for the real one, keeping the
                // optimistic entity_tag if the API response lacks it.
                lists.patch(old =>
                    old
                        ? old.map(char =>
                              char.id === context.optimisticId
                                  ? { ...result.data, entity_tag: result.data.entity_tag?.id ? result.data.entity_tag : char.entity_tag }
                                  : char,
                          )
                        : old,
                );
            }
        },
        invalidate: getPrefixes('character', 'create'),
    });
}

interface DeleteCharacterContext {
    previousLists: Array<[readonly unknown[], ICharacter[] | undefined]>;
}

export function useDeleteCharacter() {
    const lists = useOptimisticPrefixPatch<ICharacter[]>(CHARACTERS_LIST_PREFIX);

    return useCustomMutation<{ project_id: number; personage_id: number }, ResponseType<void>, DeleteCharacterContext>({
        mutationFn: ({ project_id, personage_id }) =>
            request<ResponseType<void>>(`projects/${project_id}/characters/${personage_id}`, { method: 'DELETE' }),
        onMutate: async ({ personage_id }) => {
            await lists.cancelInFlight();
            const previousLists = lists.snapshot();
            lists.patch(old => (old ? old.filter(char => char.id !== personage_id) : old));
            return { previousLists };
        },
        onError: (_err, _params, context) => {
            if (context) lists.rollback(context.previousLists);
        },
        onSuccess: (_result, { project_id, personage_id }) => {
            // Ghost-extras guard: without this a future character reusing the
            // storage slot would inherit the deleted one's mocked fields.
            removeExtras(project_id, personage_id);
        },
        invalidate: getPrefixes('character', 'delete'),
    });
}

/**
 * Merge: the character acted from (target) absorbs the picked one (source).
 * Frozen endpoint: POST projects/{p}/characters/{target_id}/merge {source_id}
 * — at swap time the mutationFn body becomes that single request. Mock phase
 * runs the «partial real merge» orchestration over existing endpoints
 * (empty fields fill, extras absorb, source deleted); scene-mention retagging
 * and photo transfer wait for the backend.
 */
export function useMergeCharacters() {
    return useCustomMutation<{ project_id: number; target_id: number; source_id: number }>({
        mutationFn: ({ project_id, target_id, source_id }) =>
            mergeCharacters(
                {
                    fetchCharacter: id =>
                        request<ResponseType<ICharacter>>(`projects/${project_id}/characters/${id}`).then(response => response.data),
                    updateCharacter: (id, data) =>
                        request<ResponseType<ICharacter>>(`projects/${project_id}/characters/${id}?_method=PUT`, {
                            method: 'POST',
                            // The server requires non-empty `categories` on every update.
                            body: JSON.stringify({ categories: resolveServerCategories(project_id, id), ...data }),
                        }),
                    deleteCharacter: id => request<ResponseType<void>>(`projects/${project_id}/characters/${id}`, { method: 'DELETE' }),
                    absorbExtras: (targetId, sourceId) => absorbExtras(project_id, targetId, sourceId),
                },
                target_id,
                source_id,
            ),
        invalidate: getPrefixes('character', 'delete'),
    });
}

/**
 * Writes the frozen-contract extras (sourse/data/characterExtras.ts). Mock
 * phase: persists to localStorage, then invalidates the character prefixes so
 * the fetchers re-enrich and every consumer updates — same flow a real
 * mutation would have. Swap: replace the mutationFn body with a request() to
 * the real endpoint.
 */
export function useUpdateCharacterExtras() {
    return useCustomMutation<{ project_id: number; character_id: number; data: Partial<CharacterExtras> }>({
        mutationFn: async ({ project_id, character_id, data }) => {
            writeExtras(project_id, character_id, data);
        },
        invalidate: getPrefixes('character', 'update'),
    });
}

export const useUpdateCharacter = useUpdate.character<
    { project_id: number; character_id: number; data: Partial<UpdateCharacterType> },
    ResponseType<ICharacter>
>({
    endpoint: ({ project_id, character_id }) => `projects/${project_id}/characters/${character_id}?_method=PUT`,
    // The server requires a non-empty `categories` on every update; patches
    // that don't set it explicitly get the character's current arc bundled in.
    body: ({ project_id, character_id, data }) =>
        'categories' in data ? data : { ...data, categories: resolveServerCategories(project_id, character_id) },
});

// --- Relation mutations -----------------------------------------------------

export const useCreateRelationCharacter = useUpdate.character<{ id: number; data: Partial<IRelationsCreate> }, ResponseType<ICharacter>>({
    endpoint: ({ id }) => `projects/${id}/character-relations`,
    body: ({ data }) => data,
});

export const useUpdateRelationCharacter = useUpdate.character<
    { project_id: number; id: number; data: Partial<IRelationsCreate> },
    ResponseType<ICharacter>
>({
    endpoint: ({ project_id, id }) => `projects/${project_id}/character-relations/${id}?_method=PUT`,
    body: ({ data }) => data,
});

export const useDeleteRelationCharacter = useDelete.character<{ project_id: number; relation_id: number }, ResponseType<ICharacter>>({
    method: 'DELETE',
    endpoint: ({ project_id, relation_id }) => `projects/${project_id}/character-relations/${relation_id}`,
});
